import type * as React from "react";

import type { RefObjectFunction } from "@fluentui/react-hooks";

import {
  setKeytipEnabled,
  useAppComponentKeytip,
  useV9KeytipEnabled,
} from "@1js/acui-keytip";
import { useAppComponentTooltips } from "@1js/acui-tooltip";
import type { AppControlBaseProps } from "@1js/acui-common";
import type {
  AppComponentKeytipProps,
  AppComponentKeytipOptions,
  AppComponentWithKeytip,
  DeriveKeytipFunction,
} from "@1js/acui-keytip";

export type AppMergedTooltipAndKeytipProps = AppControlBaseProps &
  AppComponentKeytipOptions &
  AppComponentKeytipProps & {
    /**
     * Function to derive Keytip setup based on passed keytip props.
     */
    deriveKeytip: DeriveKeytipFunction;
    /**
     * Set to true if the label should be hidden. This should only be done for controls
     * that have a well known icon that can be easily recognized without a label.
     */
    hideLabel?: boolean;
    /**
     * The text to show alongside the control. If hideLabel is set to true, this will
     * be set as the tooltip for the component.
     */
    label: string;
  };

export const useMergedTooltipAndKeytipRef = (
  props: AppMergedTooltipAndKeytipProps,
  ref: React.Ref<any>,
  componentType: AppComponentWithKeytip
): RefObjectFunction<HTMLElement | null | undefined> => {
  const {
    customTooltip,
    deriveKeytip,
    directClickRef = undefined,
    hideLabel,
    keytipProps,
    label,
    location,
    tooltipDirection,
    useDirectClick = false,
    neverShowTooltip,
  } = props;

  let finalRef = ref as RefObjectFunction<HTMLElement | null | undefined>;
  // Calculate final ref to use with tooltips and keytips
  // eslint-disable-next-line react-hooks/rules-of-hooks
  finalRef = useAppComponentTooltips(ref, {
    customTooltip,
    direction: tooltipDirection,
    hideLabel,
    label,
    location,
    neverShowTooltip,
  }).mergedRef;
  setKeytipEnabled(useV9KeytipEnabled());

  return useAppComponentKeytip(props, finalRef, componentType, deriveKeytip, {
    directClickRef,
    keytipProps,
    useDirectClick,
  });
};
